// Fees tab of the student profile: month-by-month ledger with mark paid /
// undo, per-month adjustments (concession, fine, term fee), printable
// receipts and due ledger. Uses the same /fees/student endpoints as the
// staff app, so both stay in sync.
import { useEffect, useState } from 'react';
import { Printer, Receipt, BadgePercent, Undo2, FileText } from 'lucide-react';
import { setStudentFee, setFeeMonth, payFeeMonthWith, unpayFeeMonth, setConcession, addRefund,
  getStudentServices, setStudentService, addFeeCharge, getFeeStructure } from '../../api/client';
import { Modal } from '../../ui/Form';
import Button from '../../ui/Button';
import Badge from '../../ui/Badge';
import { Card, CardHeader } from '../../ui/Card';
import PrintSheet, { PrintHeader } from '../../ui/PrintSheet';
import FeeStructureCard, { TEMPLATES as FEE_CARD_TEMPLATES } from './FeeStructureCard';
import { inr, fmtDate, PAYMENT_MODES, todayISO } from '../../ui/format';

const inputCls = 'border border-line rounded-s px-3 py-2 text-sm bg-paper-raised text-ink focus:outline-none focus:ring-2 focus:ring-transit';

const round2 = (n) => Math.round(n * 100) / 100;
const applyConcession = (gross, type, value) =>
  !type ? gross : type === 'percent' ? gross * (1 - Number(value) / 100) : gross - Number(value);

// A head-by-head breakdown (e.g. "Tuition fee ₹2,000" + "Transport ₹500")
// for a plain monthly charge, so the receipt shows what the fee is actually
// made of rather than one lump sum. Only used when the current head config
// still reconciles to what was charged — a manually Adjust'd or one-off-charge
// month already carries its own `note` and keeps the single-line receipt,
// since the structure may have changed since that month was charged.
function receiptBreakdown(receipt, fees, mandatoryHeads, services) {
  if (receipt.note) return null;
  const monthly = mandatoryHeads.filter(h => h.frequency === 'monthly');
  const optional = (services || []).filter(s => s.active);
  if (!monthly.length && !optional.length) return null;
  const gross = round2(monthly.reduce((s, h) => s + h.amount, 0) + optional.reduce((s, h) => s + h.amount, 0));
  const net = round2(applyConcession(gross, fees.concession_type, fees.concession_value));
  const base = round2(receipt.amount - (receipt.fine_amount || 0));
  if (Math.abs(net - base) > 0.5) return null;
  return { monthly, optional, gross, net };
}

export default function FeesTab({ student, fees, onFeesChange, schoolName }) {
  const [msg,       setMsg]       = useState('');
  const [busy,      setBusy]      = useState(false);
  const [setup,     setSetup]     = useState({ monthly_amount: fees?.monthly_amount || '', due_day: fees?.due_day || 5 });
  const [adjusting, setAdjusting] = useState(null);   // { year, month, amount, note, label } | null
  const [charge,    setCharge]    = useState(null);   // { ym, head_id, label, amount } | null
  const [receipt,   setReceipt]   = useState(null);   // a paid month row
  const [ledger,    setLedger]    = useState(false);
  const [paying,    setPaying]    = useState(null);   // { month row, mode, ref }
  const [concession, setConcessionForm] = useState(null); // { type, value, reason }
  const [refund,    setRefund]    = useState(null);   // { amount, reason, mode }
  const [challan,   setChallan]   = useState(false);
  const [services,  setServices]  = useState(null);   // [{ head_id, head_name, scope, active, amount, amount_override }] | null
  const [chargeHeads, setChargeHeads] = useState([]);  // one-time optional heads available for Add charge
  const [mandatoryHeads, setMandatoryHeads] = useState([]); // mandatory heads applicable to this student's class
  const [feeCard,   setFeeCard]   = useState(false);
  const [feeCardTemplate, setFeeCardTemplate] = useState('classic');

  useEffect(() => {
    getStudentServices(student.id).then(setServices).catch(() => setServices([]));
    getFeeStructure().then(hs => {
      setChargeHeads(hs.filter(h => h.frequency === 'one_time' && h.optional && (h.class_id == null || h.class_id === student.class_id)));
      setMandatoryHeads(hs.filter(h => !h.optional && (h.class_id == null || h.class_id === student.class_id)));
    }).catch(() => { setChargeHeads([]); setMandatoryHeads([]); });
  }, [student.id]);

  async function toggleService(svc, nextActive, overrideInput) {
    setBusy(true); setMsg('');
    try {
      const amount_override = overrideInput != null && overrideInput !== '' ? Number(overrideInput) : svc.amount_override;
      onFeesChange(await setStudentService(student.id, svc.head_id, { active: nextActive, amount_override }));
      setServices(await getStudentServices(student.id));
      setMsg(`${svc.head_name} ${nextActive ? 'enabled' : 'disabled'} ✅`);
    } catch (err) { setMsg('Error: ' + (err.response?.data?.error || err.message)); }
    finally { setBusy(false); }
  }

  async function run(fn, okText) {
    setBusy(true); setMsg('');
    try {
      onFeesChange(await fn());
      if (okText) setMsg(okText);
      return true;
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
      return false;
    } finally { setBusy(false); }
  }

  async function saveSetup(e) {
    e.preventDefault();
    const ok = await run(() => setStudentFee(student.id, {
      monthly_amount: Number(setup.monthly_amount), due_day: Number(setup.due_day),
    }), 'Fee plan saved ✅');
    if (ok) setSetup(s => ({ ...s, editing: false }));
  }

  async function saveAdjust(e) {
    e.preventDefault();
    const ok = await run(() => setFeeMonth(student.id, {
      year: adjusting.year, month: adjusting.month,
      amount: Number(adjusting.amount), note: adjusting.note || null,
    }), `${adjusting.label} updated ✅`);
    if (ok) setAdjusting(null);
  }

  async function saveCharge(e) {
    e.preventDefault();
    const [year, month] = charge.ym.split('-').map(Number);
    const payload = charge.head_id
      ? { year, month, head_id: Number(charge.head_id), amount: charge.amount ? Number(charge.amount) : undefined }
      : { year, month, label: charge.label, amount: Number(charge.amount) };
    const ok = await run(() => addFeeCharge(student.id, payload), 'Charge added ✅');
    if (ok) setCharge(null);
  }

  // ── Not configured yet ─────────────────────────────────────
  if (!fees?.configured || setup.editing) {
    return (
      <Card className="p-5 max-w-[460px]">
        <h3 className="font-display font-bold text-[15px] text-ink mb-1">{fees?.configured ? 'Edit fee plan' : 'Set up fees'}</h3>
        <p className="text-sm text-ink-soft mb-4">
          The monthly amount is charged automatically every month from now on. You can still change any single month later.
        </p>
        <form onSubmit={saveSetup} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Monthly fee (₹)
            <input required type="number" min="1" step="any" className={inputCls} value={setup.monthly_amount}
              onChange={e => setSetup({ ...setup, monthly_amount: e.target.value })} />
          </label>
          <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Due day of month (1–28)
            <input required type="number" min="1" max="28" className={inputCls} value={setup.due_day}
              onChange={e => setSetup({ ...setup, due_day: e.target.value })} />
          </label>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy}>Save</Button>
            {fees?.configured && <Button type="button" variant="secondary" onClick={() => setSetup(s => ({ ...s, editing: false }))}>Cancel</Button>}
          </div>
          <Msg msg={msg} />
        </form>
      </Card>
    );
  }

  const today = new Date();
  const isFuture = (m) => m.year > today.getFullYear() || (m.year === today.getFullYear() && m.month > today.getMonth() + 1);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-ink-soft mr-auto">
          {inr(fees.monthly_amount)} per month · due on day {fees.due_day}
          {fees.last_paid_month && <> · last paid {fees.months.find(m => m.paid)?.label}</>}
        </span>
        <Button variant="secondary" className="!py-1.5 !text-xs" onClick={() => setSetup({ monthly_amount: fees.monthly_amount, due_day: fees.due_day, editing: true })}>
          Edit fee plan
        </Button>
        <Button variant="secondary" className="!py-1.5 !text-xs" onClick={() => setCharge({ ym: '', head_id: '', label: '', amount: '' })}>
          Add charge
        </Button>
        <Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1.5"
          onClick={() => setConcessionForm({ type: fees.concession_type || 'percent', value: fees.concession_value ?? '', reason: fees.concession_reason || '' })}>
          <BadgePercent size={13} /> Concession
        </Button>
        <Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1.5" onClick={() => setRefund({ amount: '', reason: '', mode: 'Cash' })}>
          <Undo2 size={13} /> Refund
        </Button>
        <Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1.5" onClick={() => setLedger(true)}>
          <Printer size={13} /> Print due ledger
        </Button>
        <Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1.5" disabled={!fees.amount_due} onClick={() => setChallan(true)}>
          <FileText size={13} /> Fee challan
        </Button>
        <Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1.5" onClick={() => setFeeCard(true)}>
          <FileText size={13} /> Fee structure card
        </Button>
      </div>
      {fees.concession_type && (
        <p className="text-sm font-semibold text-transit-deep bg-transit-tint px-3 py-2 rounded-s w-fit">
          Concession: {fees.concession_type === 'percent' ? `${fees.concession_value}%` : inr(fees.concession_value)} off every month
          {fees.concession_reason ? ` — ${fees.concession_reason}` : ''}
        </p>
      )}
      <Msg msg={msg} />

      {services && services.length > 0 && (
        <Card>
          <CardHeader title="Services" />
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                <th className="px-4 py-2.5">Service</th><th className="px-4 py-2.5">Amount (₹/mo)</th>
                <th className="px-4 py-2.5"></th><th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {services.map(svc => (
                <ServiceRow key={svc.head_id} svc={svc} busy={busy} onToggle={toggleService} />
              ))}
            </tbody>
          </table>
          <p className="text-xs text-ink-soft px-4 py-2.5">Turning a service on/off updates this month's bill now (if unpaid) and every month going forward.</p>
        </Card>
      )}

      {charge && (
        <Card className="p-4">
          <form onSubmit={saveCharge} className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Month
              <input required type="month" className={inputCls} value={charge.ym} onChange={e => setCharge({ ...charge, ym: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Fee head
              <select className={inputCls} value={charge.head_id} onChange={e => setCharge({ ...charge, head_id: e.target.value })}>
                <option value="">Free-text charge…</option>
                {chargeHeads.map(h => <option key={h.id} value={h.id}>{h.head_name} ({inr(h.amount)})</option>)}
              </select>
            </label>
            {!charge.head_id && (
              <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft flex-1 min-w-[160px]">Description
                <input required maxLength={100} className={inputCls} placeholder="e.g. Sports day fee" value={charge.label} onChange={e => setCharge({ ...charge, label: e.target.value })} />
              </label>
            )}
            <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">{charge.head_id ? 'Override amount (optional)' : 'Amount (₹)'}
              <input required={!charge.head_id} type="number" min="0" step="any" className={`${inputCls} w-32`} value={charge.amount} onChange={e => setCharge({ ...charge, amount: e.target.value })} />
            </label>
            <Button type="submit" disabled={busy}>Add</Button>
            <Button type="button" variant="secondary" onClick={() => setCharge(null)}>Cancel</Button>
          </form>
          <p className="text-xs text-ink-soft mt-2">This adds to that month's existing amount — it doesn't replace it. To replace a month's total outright, use Adjust on that row instead.</p>
        </Card>
      )}

      <Card>
        <CardHeader title="Fee ledger" />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                <th className="px-4 py-2.5">Month</th><th className="px-4 py-2.5">Amount</th>
                <th className="px-4 py-2.5">Note</th><th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Paid on</th><th className="px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {fees.months.map(m => (
                <tr key={`${m.year}-${m.month}`} className="border-t border-line-soft">
                  <td className="px-4 py-3 font-semibold text-ink">{m.label}</td>
                  {adjusting && adjusting.year === m.year && adjusting.month === m.month ? (
                    <td colSpan={5} className="px-4 py-2">
                      <form onSubmit={saveAdjust} className="flex flex-wrap items-center gap-2">
                        <input required type="number" min="1" step="any" className={`${inputCls} w-28`} value={adjusting.amount}
                          onChange={e => setAdjusting({ ...adjusting, amount: e.target.value })} aria-label="Amount" />
                        <input className={`${inputCls} flex-1 min-w-[160px]`} maxLength={150} placeholder="Reason — e.g. Sibling concession, Late fine"
                          value={adjusting.note} onChange={e => setAdjusting({ ...adjusting, note: e.target.value })} aria-label="Note" />
                        <Button type="submit" disabled={busy} className="!py-1.5 !text-xs">Save</Button>
                        <Button type="button" variant="secondary" className="!py-1.5 !text-xs" onClick={() => setAdjusting(null)}>Cancel</Button>
                      </form>
                    </td>
                  ) : (
                    <>
                      <td className="px-4 py-3 tabular-nums font-bold">{inr(m.amount)}</td>
                      <td className="px-4 py-3 text-ink-soft">{m.note || '—'}</td>
                      <td className="px-4 py-3">
                        {m.paid ? <Badge tone="success">Paid</Badge>
                          : isFuture(m) ? <Badge tone="neutral">Upcoming</Badge>
                          : <Badge tone="critical">Due</Badge>}
                      </td>
                      <td className="px-4 py-3 text-ink-soft">{m.paid ? fmtDate(m.paid_at) : '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1.5 flex-wrap">
                          {m.paid ? (
                            <>
                              <Button variant="secondary" className="!px-2.5 !py-1 !text-xs flex items-center gap-1" onClick={() => setReceipt(m)}>
                                <Receipt size={12} /> Receipt
                              </Button>
                              <Button variant="ghost" className="!px-2 !py-1 !text-xs" disabled={busy}
                                onClick={() => window.confirm(`Mark ${m.label} as unpaid again?`) &&
                                  run(() => unpayFeeMonth(student.id, m.year, m.month), `${m.label} marked unpaid`)}>
                                Undo
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button className="!px-2.5 !py-1 !text-xs" disabled={busy}
                                onClick={() => setPaying({ m, mode: 'Cash', ref: '' })}>
                                Collect
                              </Button>
                              <Button variant="secondary" className="!px-2.5 !py-1 !text-xs"
                                onClick={() => setAdjusting({ year: m.year, month: m.month, amount: m.amount, note: m.note || '', label: m.label })}>
                                Adjust
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {receipt && (
        <PrintSheet title="Fee receipt" onClose={() => setReceipt(null)}>
          <PrintHeader schoolName={schoolName} docTitle="Fee Receipt"
            right={<>Receipt No. <b>{receipt.receipt_no ?? '—'}</b><br />Date: {fmtDate(receipt.paid_at)}</>} />
          <StudentLines student={student} />
          <table className="w-full text-sm border-collapse mt-4">
            <thead><tr><th className="text-left">Description</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              {(() => {
                const b = receiptBreakdown(receipt, fees, mandatoryHeads, services);
                if (!b) return (
                  <tr><td>Fee for {receipt.label}{receipt.note && !receipt.fine_amount ? ` (${receipt.note})` : ''}</td><td className="text-right">{inr(receipt.amount - (receipt.fine_amount || 0))}</td></tr>
                );
                return (
                  <>
                    {b.monthly.map(h => <tr key={`m${h.id}`}><td>{h.head_name} — {receipt.label}</td><td className="text-right">{inr(h.amount)}</td></tr>)}
                    {b.optional.map(h => <tr key={`o${h.head_id}`}><td>{h.head_name} — {receipt.label}</td><td className="text-right">{inr(h.amount)}</td></tr>)}
                    {fees.concession_type && <tr><td>Concession</td><td className="text-right">-{inr(round2(b.gross - b.net))}</td></tr>}
                  </>
                );
              })()}
              {receipt.fine_amount > 0 && <tr><td>Late fine</td><td className="text-right">{inr(receipt.fine_amount)}</td></tr>}
              <tr><td className="font-bold">Total received</td><td className="text-right font-bold">{inr(receipt.amount)}</td></tr>
            </tbody>
          </table>
          {receipt.payment_mode && (
            <p className="text-[12.5px] text-[#405058] mt-3">Paid by <b>{receipt.payment_mode}</b>{receipt.payment_ref ? <> · Ref. <b>{receipt.payment_ref}</b></> : null}</p>
          )}
          <p className="text-[12.5px] text-[#405058] mt-4">
            Current outstanding balance: <b>{inr(fees.amount_due)}</b>
          </p>
          <Signature />
        </PrintSheet>
      )}

      {ledger && (
        <PrintSheet title="Due ledger" onClose={() => setLedger(false)}>
          <PrintHeader schoolName={schoolName} docTitle="Fee Due Ledger" right={<>Printed {fmtDate(new Date().toISOString())}</>} />
          <StudentLines student={student} />
          <table className="w-full text-sm border-collapse mt-4">
            <thead><tr><th className="text-left">Month</th><th className="text-left">Note</th><th className="text-left">Status</th><th className="text-left">Paid on</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              {fees.months.slice().reverse().map(m => (
                <tr key={`${m.year}-${m.month}`}>
                  <td>{m.label}</td><td>{m.note || ''}</td>
                  <td>{m.paid ? 'Paid' : isFuture(m) ? 'Upcoming' : 'Due'}</td>
                  <td>{m.paid ? fmtDate(m.paid_at) : ''}</td>
                  <td className="text-right">{inr(m.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end gap-8 mt-4 text-sm">
            <span>Paid: <b>{inr(fees.amount_paid)}</b></span>
            <span>Balance due: <b>{inr(fees.amount_due)}</b></span>
          </div>
          {fees.amount_due > 0 && <p className="text-[12.5px] text-[#405058] mt-2">Please clear the balance by {fmtDate(fees.next_due_date)}.</p>}
          <Signature />
        </PrintSheet>
      )}

      {paying && (
        <Modal title={`Collect fee — ${paying.m.label}`} onClose={() => setPaying(null)} width={420}>
          <form className="flex flex-col gap-3" onSubmit={async e => {
            e.preventDefault();
            const ok = await run(() => payFeeMonthWith(student.id, paying.m.year, paying.m.month, { mode: paying.mode, ref: paying.ref }),
              `${paying.m.label} paid (${paying.mode}) ✅`);
            if (ok) setPaying(null);
          }}>
            <p className="text-sm text-ink">Amount: <b className="text-lg">{inr(paying.m.amount)}</b>{paying.m.fine_amount > 0 && <span className="text-ink-soft"> (incl. {inr(paying.m.fine_amount)} late fine)</span>}</p>
            <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Payment mode
              <select className={inputCls} value={paying.mode} onChange={e => setPaying({ ...paying, mode: e.target.value })}>
                {PAYMENT_MODES.map(m => <option key={m}>{m}</option>)}
              </select>
            </label>
            {paying.mode !== 'Cash' && (
              <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">{paying.mode === 'Cheque' ? 'Cheque number' : 'Transaction / UTR number'}
                <input className={inputCls} maxLength={60} value={paying.ref} onChange={e => setPaying({ ...paying, ref: e.target.value })} />
              </label>
            )}
            <div className="flex gap-2"><Button type="submit" disabled={busy}>Mark paid</Button><Button type="button" variant="secondary" onClick={() => setPaying(null)}>Cancel</Button></div>
          </form>
        </Modal>
      )}

      {concession && (
        <Modal title="Concession" onClose={() => setConcessionForm(null)} width={440}>
          <form className="flex flex-col gap-3" onSubmit={async e => {
            e.preventDefault();
            const ok = await run(() => setConcession(student.id, { type: concession.type, value: Number(concession.value), reason: concession.reason }), 'Concession saved ✅');
            if (ok) setConcessionForm(null);
          }}>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Type
                <select className={inputCls} value={concession.type} onChange={e => setConcessionForm({ ...concession, type: e.target.value })}>
                  <option value="percent">% off</option><option value="fixed">₹ off</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">{concession.type === 'percent' ? 'Percent' : 'Rupees'} per month
                <input required type="number" min="0.01" max={concession.type === 'percent' ? 100 : undefined} step="any" className={inputCls}
                  value={concession.value} onChange={e => setConcessionForm({ ...concession, value: e.target.value })} />
              </label>
            </div>
            <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Reason
              <input className={inputCls} maxLength={100} placeholder="e.g. Sibling, Staff ward, Merit" value={concession.reason} onChange={e => setConcessionForm({ ...concession, reason: e.target.value })} />
            </label>
            <p className="text-xs text-ink-soft">Applies from this month (if unpaid) onward. Paid months are never changed.</p>
            <div className="flex gap-2">
              <Button type="submit" disabled={busy}>Save</Button>
              {fees.concession_type && (
                <Button type="button" variant="danger" disabled={busy} onClick={async () => {
                  const ok = await run(() => setConcession(student.id, { type: null }), 'Concession removed');
                  if (ok) setConcessionForm(null);
                }}>Remove concession</Button>
              )}
              <Button type="button" variant="secondary" onClick={() => setConcessionForm(null)}>Cancel</Button>
            </div>
          </form>
        </Modal>
      )}

      {refund && (
        <Modal title="Record refund" onClose={() => setRefund(null)} width={420}>
          <form className="flex flex-col gap-3" onSubmit={async e => {
            e.preventDefault();
            setBusy(true); setMsg('');
            try {
              await addRefund(student.id, { amount: Number(refund.amount), reason: refund.reason, mode: refund.mode });
              setMsg(`Refund of ${inr(refund.amount)} recorded ✅ — see the Payments tab`);
              setRefund(null);
            } catch (err) { setMsg('Error: ' + (err.response?.data?.error || err.message)); }
            finally { setBusy(false); }
          }}>
            <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Amount (₹)
              <input required type="number" min="1" step="any" className={inputCls} value={refund.amount} onChange={e => setRefund({ ...refund, amount: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Reason
              <input required maxLength={200} className={inputCls} placeholder="e.g. Left school mid-term" value={refund.reason} onChange={e => setRefund({ ...refund, reason: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Paid back by
              <select className={inputCls} value={refund.mode} onChange={e => setRefund({ ...refund, mode: e.target.value })}>
                {PAYMENT_MODES.map(m => <option key={m}>{m}</option>)}
              </select>
            </label>
            <div className="flex gap-2"><Button type="submit" disabled={busy}>Save refund</Button><Button type="button" variant="secondary" onClick={() => setRefund(null)}>Cancel</Button></div>
          </form>
        </Modal>
      )}

      {challan && (
        <PrintSheet title="Fee challan" onClose={() => setChallan(false)}>
          {['School copy', 'Bank copy', 'Parent copy'].map((copy, i) => (
            <div key={copy} className={i ? 'border-t-2 border-dashed border-[#9aa8a6] pt-5 mt-5' : ''}>
              <PrintHeader schoolName={schoolName} docTitle={`Fee Challan · ${copy}`} right={<>Date: {fmtDate(todayISO())}<br />{fees.next_due_date < todayISO() ? <>Overdue since {fmtDate(fees.next_due_date)}</> : <>Pay by: {fmtDate(fees.next_due_date)}</>}</>} />
              <StudentLines student={student} />
              <table className="w-full text-sm border-collapse mt-3">
                <thead><tr><th className="text-left">Month</th><th className="text-left">Details</th><th className="text-right">Amount</th></tr></thead>
                <tbody>
                  {fees.months.filter(m => !m.paid && !isFuture(m)).reverse().map(m => (
                    <tr key={`${m.year}-${m.month}`}><td>{m.label}</td>
                      <td>{m.fine_amount > 0 ? `Monthly fee ${inr(m.amount - m.fine_amount)} + late fine ${inr(m.fine_amount)}` : (m.note || 'Monthly fee')}</td>
                      <td className="text-right">{inr(m.amount)}</td></tr>
                  ))}
                  <tr><td colSpan={2} className="font-bold">Total payable</td><td className="text-right font-bold">{inr(fees.amount_due)}</td></tr>
                </tbody>
              </table>
              <div className="flex justify-between mt-8 text-xs text-[#405058]">
                <span>Depositor's signature</span><span>Received by (stamp)</span>
              </div>
            </div>
          ))}
        </PrintSheet>
      )}

      {feeCard && (
        <PrintSheet title="Fee structure card" onClose={() => setFeeCard(false)}>
          <div className="flex items-center gap-2 mb-4 no-print">
            <span className="text-xs font-bold text-ink-soft">Template:</span>
            {FEE_CARD_TEMPLATES.map(t => (
              <button key={t} type="button" onClick={() => setFeeCardTemplate(t)}
                className={`px-3 py-1 rounded-full text-xs font-bold border capitalize ${feeCardTemplate === t ? 'bg-transit text-white border-transit' : 'border-line text-ink'}`}>
                {t}
              </button>
            ))}
          </div>
          <FeeStructureCard template={feeCardTemplate} schoolName={schoolName} student={student} fees={fees}
            mandatoryMonthly={mandatoryHeads.filter(h => h.frequency === 'monthly')}
            mandatoryOneTime={mandatoryHeads.filter(h => h.frequency === 'one_time')}
            optionalActive={(services || []).filter(s => s.active)} />
        </PrintSheet>
      )}
    </div>
  );
}

function StudentLines({ student }) {
  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-[13.5px]">
      <span>Student: <b>{student.name}</b></span>
      <span>Class: <b>{student.class_name || '—'}</b>{student.roll_number ? <> · Roll <b>{student.roll_number}</b></> : null}</span>
      <span>Admission No.: <b>{student.admission_no || '—'}</b></span>
      <span>Parent: <b>{student.father_name || student.guardian_name || '—'}</b></span>
    </div>
  );
}

function ServiceRow({ svc, busy, onToggle }) {
  const [amount, setAmount] = useState(svc.amount);
  return (
    <tr className="border-t border-line-soft">
      <td className="px-4 py-3 font-semibold">
        {svc.head_name}{' '}{svc.scope === 'all_classes' && <Badge tone="info">All classes</Badge>}
      </td>
      <td className="px-4 py-3">
        <input type="number" min="0" step="any" className={`${inputCls} w-24`} value={amount}
          onChange={e => setAmount(e.target.value)} />
      </td>
      <td className="px-4 py-3 text-ink-soft">{svc.amount_override != null ? 'Custom' : 'Default'}</td>
      <td className="px-4 py-3 text-right">
        <div className="flex gap-1.5 justify-end">
          {svc.active && Number(amount) !== Number(svc.amount) && (
            <Button variant="secondary" className="!px-3 !py-1 !text-xs" disabled={busy} onClick={() => onToggle(svc, true, amount)}>
              Save amount
            </Button>
          )}
          <Button variant={svc.active ? 'danger' : 'secondary'} className="!px-3 !py-1 !text-xs" disabled={busy}
            onClick={() => onToggle(svc, !svc.active, amount)}>
            {svc.active ? 'Turn off' : 'Turn on'}
          </Button>
        </div>
      </td>
    </tr>
  );
}

function Signature() {
  return (
    <div className="flex justify-end mt-12">
      <div className="border-t border-[#101b22] pt-1 px-6 text-xs text-[#405058]">Authorised signature</div>
    </div>
  );
}

function Msg({ msg }) {
  if (!msg) return null;
  return (
    <p className={`text-sm font-bold px-3 py-2 rounded-s w-fit ${msg.startsWith('Error') ? 'bg-critical-tint text-critical' : 'bg-success-tint text-success'}`}>
      {msg}
    </p>
  );
}
